import type {
  KgInsightMention,
  KgInsightMentionsResponse,
} from '@api/api/kg-insights';
import { type UseQueryResult, useQueries } from '@tanstack/react-query';

import {
  fetchAllMentions,
  fetchBasketMentions,
  QUERY_KEY_THEME_MENTIONS,
} from '../services/theme-mentions-service';

type ThemeMentionScope =
  | {
      kind: 'theme';
      themeIds: string[];
      companyIds: undefined;
      enabled: boolean;
    }
  | {
      kind: 'basket';
      themeIds: string[];
      companyIds: string[];
      enabled: boolean;
    };

const combineThemeMentions = (
  results: UseQueryResult<KgInsightMentionsResponse>[]
) => {
  const isLoading = results.some(result => result.isLoading);
  const isError = results.some(result => result.isError);
  const mentions: KgInsightMention[] = [];
  const seenInsights = new Set<string>();

  for (const result of results) {
    for (const mention of result.data?.mentions ?? []) {
      if (seenInsights.has(mention.insight_id)) {
        continue;
      }

      mentions.push(mention);
      seenInsights.add(mention.insight_id);
    }
  }

  const hasData = results.some(result => result.data !== undefined);

  return {
    data: hasData ? { mentions } : undefined,
    isLoading,
    isError,
  };
};

export const useThemeMentions = (scope: ThemeMentionScope) => {
  const { themeIds, companyIds, enabled } = scope;

  return useQueries({
    queries: themeIds.map(themeId => ({
      queryKey: [QUERY_KEY_THEME_MENTIONS, themeId, companyIds],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        scope.kind === 'basket'
          ? fetchBasketMentions(themeId, scope.companyIds, signal)
          : fetchAllMentions(themeId, signal),
      enabled,
    })),
    combine: combineThemeMentions,
  });
};
